import React, { useEffect, useState } from 'react';
import { useVoice } from '../VoiceContext';
import {
  Alert, Container, Box, Typography, Select, MenuItem, TextField, Button, Slider, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useLang } from '../i18n';
import { getStudySettings } from '../api';
import { pickVoice, saveVoiceName, speak, voicesFor } from '../voice';

const SAMPLES = {
  it: '"La vita è come una bicicletta: per mantenere l\'equilibrio, devi continuare a muoverti avanti." - Albert Einstein',
  ru: 'Приве́т! Меня́ зову́т А́нна. Я живу́ в Москве́ и рабо́таю в шко́ле.',
};

const VoiceSettings = () => {
  const { t } = useLang();
  const { voices, loading: voicesLoading } = useVoice();
  const [target, setTarget] = useState('it');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [text, setText] = useState(SAMPLES.it);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [saved, setSaved] = useState(false);

  // по умолчанию — язык, который учит пользователь
  useEffect(() => {
    if (!localStorage.getItem('access_token')) return;
    getStudySettings().then((s) => {
      if (s?.target) {
        setTarget(s.target);
        setText(SAMPLES[s.target] || SAMPLES.it);
      }
    }).catch(() => {});
  }, []);

  const list = voicesFor(voices, target);
  useEffect(() => {
    const voice = pickVoice(voices, target);
    setSelectedVoice(voice ? voice.name : '');
  }, [voices, target]);

  // если голосов нет вообще, VoiceContext так и остаётся «загружается» — проверяем сами через пару секунд
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (list.length > 0) { setMissing(false); return undefined; }
    const timer = setTimeout(() => {
      const all = window.speechSynthesis ? window.speechSynthesis.getVoices() : [];
      setMissing(voicesFor(all, target).length === 0);
    }, 2500);
    return () => clearTimeout(timer);
  }, [list.length, target]);

  const changeTarget = (next) => {
    if (!next) return;
    setTarget(next);
    setText(SAMPLES[next]);
    setSaved(false);
  };

  const speakText = () => {
    if (selectedVoice) saveVoiceName(target, selectedVoice);
    speak(voices, text, target, { rate, pitch });
  };

  const setDefaultVoice = () => {
    saveVoiceName(target, selectedVoice);
    setSaved(true);
  };

  return (
    <Container sx={{ mt: 4 }} maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
      <Typography variant="h4" gutterBottom>{t('voice.title')}</Typography>
      <ToggleButtonGroup size="small" exclusive value={target} onChange={(_, v) => changeTarget(v)} sx={{ mb: 2 }}>
        <ToggleButton value="it">🇮🇹 {t('voice.lang.it')}</ToggleButton>
        <ToggleButton value="ru">🇷🇺 {t('voice.lang.ru')}</ToggleButton>
      </ToggleButtonGroup>
      <Select
        fullWidth
        value={selectedVoice}
        onChange={(e) => { setSelectedVoice(e.target.value); setSaved(false); }}
        sx={{ mb: 2 }}
        disabled={voicesLoading || list.length === 0}
      >
        {list.map((voice) => (
          <MenuItem key={voice.name} value={voice.name}>
            {voice.name} ({voice.lang})
          </MenuItem>
        ))}
      </Select>
      {missing ? (
        <Alert severity="warning" sx={{ mb: 4, width: '100%' }}>{t(`voice.missing.${target}`)}</Alert>
      ) : (
        <Typography variant="body2" sx={{ mb: 4 }}>{t('voice.hint')}</Typography>
      )}

      <Typography variant="h5" gutterBottom>{t('voice.textTitle')}</Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        sx={{ mb: 2 }}
        inputProps={{ lang: target }}
      />

      <Typography gutterBottom>{t('voice.rate', { v: rate })}</Typography>
      <Slider
        value={rate}
        min={0.1}
        max={2}
        step={0.1}
        onChange={(e, newValue) => setRate(newValue)}
        sx={{ mb: 2 }}
      />

      <Typography gutterBottom>{t('voice.pitch', { v: pitch })}</Typography>
      <Slider
        value={pitch}
        min={0.1}
        max={2}
        step={0.1}
        onChange={(e, newValue) => setPitch(newValue)}
        sx={{ mb: 2 }}
      />

      <Button variant="contained" fullWidth onClick={speakText} sx={{ mb: 2 }}>
        {t('voice.speak')}
      </Button>
      <Button variant="outlined" fullWidth onClick={setDefaultVoice} disabled={!selectedVoice}>
        {t('voice.setDefault')}
      </Button>
      {saved && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {t('voice.saved', { name: selectedVoice })}
        </Typography>
      )}
      </Box>
    </Container>
  );
};

export default VoiceSettings;
